/**
 * Logica di generazione PDF per Bolletto.
 *
 * Responsabilità:
 * - Conversione immagini cross-origin in base64 (via proxy backend)
 * - Inversione pixel per loghi bianchi su sfondo scuro
 * - Rendering HTML → Canvas → PDF (singolo e batch)
 *
 * Il batch PDF usa un OffscreenRenderer dedicato per rendering deterministico
 * senza dipendere da timing UI o mutazione dello stato React visibile.
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API } from '../../utils/constants';

// ─── Image utilities ─────────────────────────────────────

/**
 * Inverte tutti i pixel non-trasparenti a bianco.
 * Sostituisce il CSS `brightness(0) invert(1)` non supportato da html2canvas.
 * @param {string} base64 - immagine sorgente in base64
 * @returns {Promise<string>} immagine invertita in base64
 */
export const invertImageToWhite = (base64) => {
  return new Promise((resolve) => {
    const tempImg = new Image();
    tempImg.onload = () => {
      const c = document.createElement('canvas');
      c.width = tempImg.width;
      c.height = tempImg.height;
      const ctx = c.getContext('2d');
      ctx.drawImage(tempImg, 0, 0);
      const imageData = ctx.getImageData(0, 0, c.width, c.height);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] > 0) { d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; }
      }
      ctx.putImageData(imageData, 0, 0);
      resolve(c.toDataURL('image/png'));
    };
    tempImg.src = base64;
  });
};

/**
 * Converte tutte le immagini `[data-pdf-img]` in un container in base64.
 * Usa il proxy backend `/api/proxy-image` per bypassare CORS.
 * Se l'immagine ha le classi `brightness-0 invert`, inverte i pixel a bianco.
 * @param {HTMLElement} container - elemento DOM che contiene le immagini
 */
export const preloadImagesForPdf = async (container) => {
  const imgs = container.querySelectorAll('img[data-pdf-img]');
  for (const img of imgs) {
    try {
      if (img.src.startsWith('data:')) continue;
      const proxyUrl = `${API}/proxy-image?url=${encodeURIComponent(img.src)}`;
      const resp = await fetch(proxyUrl, { credentials: 'include' });
      const blob = await resp.blob();
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      img.setAttribute('data-original-src', img.src);
      const needsInvert = img.classList.contains('invert') && img.classList.contains('brightness-0');
      if (needsInvert) {
        const invertedBase64 = await invertImageToWhite(base64);
        img.src = invertedBase64;
        img.setAttribute('data-original-class', img.className);
        img.classList.remove('brightness-0', 'invert');
      } else {
        img.src = base64;
      }
    } catch (e) {
      console.warn('Failed to preload image:', img.src, e);
    }
  }
};

/**
 * Ripristina le immagini originali dopo il rendering PDF.
 * @param {HTMLElement} container
 */
export const restoreImagesAfterPdf = (container) => {
  const imgs = container.querySelectorAll('img[data-original-src]');
  for (const img of imgs) {
    img.src = img.getAttribute('data-original-src');
    img.removeAttribute('data-original-src');
    if (img.hasAttribute('data-original-class')) {
      img.className = img.getAttribute('data-original-class');
      img.removeAttribute('data-original-class');
    }
  }
};

// ─── Selettori elementi da nascondere nel PDF ────────────

const HIDE_SELECTORS = '[data-testid="download-pdf-btn"], [data-testid="manual-cte-selector"], [data-testid="frozen-badge"], [data-testid="finalize-btn"], [data-testid="finalized-toast"], [data-testid="auto-corrected-alert"], [data-testid="totals-invalid-alert"]';

// ─── Configurazione qualità/dimensione PDF ───────────────
// JPEG 0.85 produce file ~70% più leggeri di PNG con qualità visivamente identica
// per documenti con testo + grafici come questo. PNG mantenuto solo se ci sono
// trasparenze critiche (qui non rilevanti perché il background è bianco).
const JPEG_QUALITY = 0.85;
const CANVAS_SCALE = 2; // 2x per nitidezza testo, equivalente a ~150dpi A4

/**
 * Nasconde gli elementi non-stampabili, esegue html2canvas, ripristina tutto.
 * @param {HTMLElement} element - ref al container dell'analisi
 * @returns {Promise<HTMLCanvasElement>}
 */
const captureElement = async (element) => {
  const hideEls = element.querySelectorAll(HIDE_SELECTORS);
  hideEls.forEach((el) => { el.style.display = 'none'; });

  await preloadImagesForPdf(element);

  const canvas = await html2canvas(element, {
    scale: CANVAS_SCALE,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    imageTimeout: 15000,
  });

  restoreImagesAfterPdf(element);
  hideEls.forEach((el) => { el.style.display = ''; });

  return canvas;
};

// ─── Download PDF singolo ────────────────────────────────

/**
 * Genera e scarica il PDF per una singola analisi.
 * Output: JPEG compresso dentro PDF con compression abilitata.
 * @param {HTMLElement} element - ref al container dell'analisi renderizzata
 * @param {Object} analysis - dati dell'analisi corrente
 */
export const downloadSinglePdf = async (element, analysis) => {
  const canvas = await captureElement(element);
  const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const ratio = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
  const imgX = (pdfWidth - canvas.width * ratio) / 2;

  pdf.addImage(imgData, 'JPEG', imgX, 0, canvas.width * ratio, canvas.height * ratio, undefined, 'FAST');

  const clientName = analysis.competitor_data?.client_name || 'Analisi';
  const servicePrefix = analysis.competitor_data?.service_type === 'gas' ? 'GAS' : 'EE';
  pdf.save(`Bolletto_${servicePrefix}_${clientName.replace(/\s+/g, '_')}.pdf`);
};

// ─── Download PDF batch ──────────────────────────────────

/**
 * Genera un PDF multi-pagina per un set di analisi.
 * Usa OffscreenRenderer per rendering deterministico: nessun setTimeout,
 * nessuna mutazione della UI visibile.
 *
 * Politica errori: skip con log. Se un'analisi fallisce, viene saltata
 * e il batch continua. Il PDF finale contiene le analisi riuscite.
 *
 * @param {Object} params
 * @param {Array} params.selectedAnalyses - analisi da esportare
 * @param {string} params.fileName - nome file output
 * @param {Function} [params.onProgress] - callback (current, total) per progress bar futura
 * @returns {Promise<{exported: number, failed: number, errors: string[]}>}
 */
export const downloadBatchPdf = async ({ selectedAnalyses, fileName, onProgress }) => {
  const { captureAnalysisOffscreen } = await import('./OffscreenRenderer');

  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  let isFirstPage = true;
  let exported = 0;
  const errors = [];

  for (let i = 0; i < selectedAnalyses.length; i++) {
    const analysis = selectedAnalyses[i];
    try {
      const canvas = await captureAnalysisOffscreen(analysis);
      const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const ratio = pdfWidth / canvas.width;
      const scaledHeight = canvas.height * ratio;
      const totalPages = Math.ceil(scaledHeight / pdfHeight);

      for (let p = 0; p < totalPages; p++) {
        if (!isFirstPage) pdf.addPage();
        isFirstPage = false;
        pdf.addImage(imgData, 'JPEG', 0, -(p * pdfHeight), pdfWidth, scaledHeight, undefined, 'FAST');
      }
      exported++;
    } catch (e) {
      const clientName = analysis.competitor_data?.client_name || `Analisi ${i + 1}`;
      console.error(`Batch PDF: errore per "${clientName}":`, e);
      errors.push(clientName);
    }

    if (onProgress) onProgress(i + 1, selectedAnalyses.length);
  }

  if (exported > 0) {
    pdf.save(`${fileName.replace(/\.pdf$/i, '')}.pdf`);
  }

  return { exported, failed: errors.length, errors };
};
